// ---------------------------------------------------------------------------
// Lightbox — one media item, full size, with its story in a side rail: who put
// it here, when, how big, and where it lives (a task, or the shared library's
// audience). Video seeking is live because files.routes.ts already speaks
// Range/206 — the <video> element just works against item.url.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { ReactNode } from 'react';
import type { MediaItem } from '@cg-projects/shared';
import { Download, FileText, Music, X } from 'lucide-react';
import { Icon } from '@/core/ui/icons';
import { Button } from '@/core/ui/atoms';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { formatDate } from '@/core/ui/helpers';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { humanSize, useMediaStore } from './mediaStore';

/** Right-rail meta row: label above value — same recipe as the task panel's Prop. */
function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="py-2">
      <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-faint">{label}</div>
      <div className="min-w-0 break-words text-sm text-ink">{children}</div>
    </div>
  );
}

export function Lightbox({
  item,
  audienceLabel,
  onClose,
  closePanel,
}: {
  item: MediaItem;
  /** "Everyone at CG" or a project name — MediaPage resolves it (it owns the area map). */
  audienceLabel: string;
  onClose: () => void;
  /** Closes the whole Media panel — "Open task" needs it, or the task panel
   *  would render UNDER the media overlay. */
  closePanel: () => void;
}) {
  // fromInputs: true = capture-phase + stopPropagation, so THIS overlay claims
  // Esc and MediaPage's own (bubble-phase) Esc doesn't close the panel on the
  // same press. Same stacking rule the search modal uses over the task panel.
  useEscapeToClose(onClose, { fromInputs: true });

  const me = useAuthStore((s) => s.user);
  const openTaskDetail = useTaskDetailStore((s) => s.open);
  const remove = useMediaStore((s) => s.remove);
  const [deleting, setDeleting] = useState(false);

  // Heuristic only — the server also lets a project's lead delete, and it is the
  // real gate either way; a 403 comes back as a toast with the server's words.
  const canDelete = me !== null && (me.isAdmin || (item.uploaderUid !== null && item.uploaderUid === me.uid));

  const goToTask = () => {
    if (item.taskId === null) return;
    // Close only if it opened — open() refuses when the user keeps an unsaved
    // draft elsewhere, and this overlay is their way back.
    void openTaskDetail(item.taskId).then((ok) => {
      if (ok) { onClose(); closePanel(); }
    });
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${item.name}"? There is no undo.`)) return;
    setDeleting(true);
    const ok = await remove(item.id); // store toasts the failure with the server's message
    setDeleting(false);
    if (ok) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex animate-fade-in flex-col bg-page/90 md:flex-row"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Media zone — clicking the empty space around the file closes too. */}
      <div
        className="flex min-h-0 min-w-0 flex-1 items-center justify-center p-4 md:p-8"
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {item.kind === 'image' && (
          <img src={item.url} alt={item.name} className="max-h-[85vh] max-w-full rounded-md object-contain" />
        )}
        {item.kind === 'video' && (
          <video src={item.url} controls autoPlay playsInline className="max-h-[85vh] max-w-full rounded-md" />
        )}
        {item.kind === 'audio' && (
          <div className="flex flex-col items-center gap-3">
            <span className="text-faint"><Icon i={Music} size={20} /></span>
            <span className="max-w-md break-words text-center text-sm text-ink">{item.name}</span>
            <audio src={item.url} controls className="w-80 max-w-full" />
          </div>
        )}
        {item.kind === 'other' && (
          <div className="flex flex-col items-center gap-3">
            <span className="text-faint"><Icon i={FileText} size={20} /></span>
            <span className="max-w-md break-words text-center text-sm text-ink">{item.name}</span>
            <span className="text-xs text-faint">No preview for this file type — download to view it.</span>
          </div>
        )}
      </div>

      {/* Meta rail */}
      <div className="flex max-h-[45vh] w-full shrink-0 flex-col overflow-y-auto border-t border-line bg-canvas p-4 shadow-modal md:max-h-none md:w-72 md:border-l md:border-t-0">
        <div className="mb-2 flex items-start gap-2">
          <div className="min-w-0 flex-1 break-words text-sm font-semibold text-ink">{item.name}</div>
          <button onClick={onClose} className="rounded p-1 text-muted transition hover:bg-hover hover:text-ink" title="Close">
            <Icon i={X} size={16} />
          </button>
        </div>

        <div className="divide-y divide-line">
          <Meta label="Uploaded by">{item.uploaderName ?? '—'}</Meta>
          <Meta label="Added">{formatDate(item.date) || '—'}</Meta>
          <Meta label="Size">{humanSize(item.fileSize) || '—'}</Meta>
          {item.isLibrary ? (
            <Meta label="Shared with">{audienceLabel}</Meta>
          ) : (
            <Meta label="Lives on">
              <div className="min-w-0">
                {item.areaName && <div className="truncate text-xs text-faint">{item.areaName}</div>}
                <div className="break-words">{item.taskName ?? 'A task'}</div>
                {item.taskId !== null && (
                  <Button size="sm" className="mt-2" onClick={goToTask}>Open task</Button>
                )}
              </div>
            </Meta>
          )}
        </div>

        <div className="mt-auto flex items-center gap-2 pt-4">
          <a
            href={item.url}
            download={item.name}
            className="inline-flex h-7 items-center justify-center gap-1.5 rounded-md border border-line px-2.5 text-xs font-medium text-ink transition hover:bg-hover"
          >
            <Icon i={Download} size={14} /> Download
          </a>
          {canDelete && (
            <Button variant="danger" size="sm" loading={deleting} onClick={() => void doDelete()}>
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
